import { useState, useEffect } from 'react';
import { fetchSendCooperationLead } from '@/service/api/lead';
import Toast from '@/components/common/Toast';

interface LeadsSubmitModalProps {
  open: boolean;
  talent: Api.Provider.ProviderPublicProfileResponse | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const PHONE_REGEX = /^1[3-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LeadsSubmitModal({
  open,
  talent,
  onClose,
  onSuccess,
}: LeadsSubmitModalProps) {
  const [formData, setFormData] = useState({
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    contact_wechat: '',
    intention: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);

  // 初始化与重置表单数据为空
  useEffect(() => {
    if (open) {
      setFormData({
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        contact_wechat: '',
        intention: '',
      });
      setSubmitting(false);
    }
  }, [open]);

  if (!open || !talent) return null;

  const displayName = talent.display_name || talent.enterprise_name_masked || '创作者';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.contact_name.trim()) {
      setToast({ message: '请输入您的姓名', type: 'warning' });
      return;
    }
    const phone = formData.contact_phone.trim();
    if (!phone) {
      setToast({ message: '请输入手机号', type: 'warning' });
      return;
    }
    if (!PHONE_REGEX.test(phone)) {
      setToast({ message: '请输入正确的11位手机号码', type: 'warning' });
      return;
    }
    const email = formData.contact_email.trim();
    if (!email) {
      setToast({ message: '请输入联系邮箱', type: 'warning' });
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      setToast({ message: '请输入正确的电子邮箱地址', type: 'warning' });
      return;
    }
    if (!formData.intention.trim()) {
      setToast({ message: '请填写合作意向说明', type: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      await fetchSendCooperationLead(talent.id, {
        contact_name: formData.contact_name.trim(),
        contact_phone: formData.contact_phone.trim(),
        contact_email: formData.contact_email.trim(),
        contact_wechat: formData.contact_wechat.trim(),
        intention: formData.intention.trim(),
        lead_type: 'COOPERATION',
      });

      setToast({ message: '合作意向提交成功！对方将收到提醒。', type: 'success' });
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('提交合作意向失败:', err);
      setToast({
        message: err instanceof Error ? err.message : '提交合作意向失败，请稍后重试',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-7 shadow-2xl relative animate-in fade-in zoom-in duration-200 my-8">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-foreground-400 hover:text-foreground-700 w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          {/* Modal Header */}
          <div className="flex items-center gap-3.5 mb-6">
            <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center text-primary-600 shrink-0">
              <i className="ri-chat-smile-2-line text-2xl"></i>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-bold text-foreground-950 truncate">发起合作意向</h3>
              <p className="text-xs text-foreground-500 truncate mt-0.5">
                面向人才 <span className="font-semibold text-foreground-800">{displayName}</span> 提交合作需求线索
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Contact Name & Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                  联系人姓名 <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="请输入姓名"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                  联系电话 <span className="text-rose-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  placeholder="请输入手机号"
                  maxLength={11}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 transition-all"
                  required
                />
              </div>
            </div>

            {/* Email & WeChat */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                  联系邮箱 <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="请输入电子邮箱"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 transition-all"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">微信号</label>
                <input
                  type="text"
                  value={formData.contact_wechat}
                  onChange={(e) => setFormData({ ...formData, contact_wechat: e.target.value })}
                  placeholder="请输入微信号"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 transition-all"
                />
              </div>
            </div>

            {/* Intention Details */}
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                合作意向说明 <span className="text-rose-500">*</span>
              </label>
              <textarea
                value={formData.intention}
                onChange={(e) => setFormData({ ...formData, intention: e.target.value })}
                placeholder="请简要说明您的项目背景、需求预算及合作意向..."
                rows={4}
                maxLength={500}
                className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 transition-all resize-none"
                required
              ></textarea>
              <p className="text-[11px] text-foreground-400 mt-1 text-right">
                {formData.intention.length}/500
              </p>
            </div>

            {/* Info notice */}
            <div className="p-3.5 rounded-xl bg-gray-50 border border-gray-100">
              <p className="text-xs text-foreground-500 flex items-start gap-1.5 leading-relaxed">
                <i className="ri-information-line text-primary-500 text-sm shrink-0 mt-0.5"></i>
                <span>提交后对方可在「线索中心 / 收到的线索」中查看到您的合作需求与联系方式。</span>
              </p>
            </div>

            {/* Form actions */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-5 py-2.5 rounded-xl border border-gray-200 text-foreground-700 text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer shadow-xs disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill text-base"></i>
                    确认提交
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}
